import { MasterFilesType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';
import { ProductionApi } from './productionApi';
import { ReleasesApi } from './releasesApi';

const invalidateFrameDrawingConsumers = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: any,
) => {
  let shouldInvalidate = true;
  try {
    await queryFulfilled;
  } catch (rejection: any) {
    // The upload endpoint returns 207 after committing successful items.
    shouldInvalidate = rejection?.error?.status === 207;
  }

  if (!shouldInvalidate) {
    return;
  }

  dispatch(
    ProductionApi.Api.util.invalidateTags([
      {
        type: 'Production',
        id: 'frame-drawing-consumers',
      },
    ]),
  );
  dispatch(
    ReleasesApi.Api.util.invalidateTags([
      {
        type: 'Releases',
        id: 'frame-drawing-summaries',
      },
    ]),
  );
};

export type args = {
  // Frame Master Files
  postFrameMasterFiles: ApiType<MasterFilesType.PostFrameMasterFiles>['args'];
  getFrameMasterFilesAll: ApiType<MasterFilesType.GetFrameMasterFilesAll>['args'];
  getFrameMasterFileRevisionsById: ApiType<MasterFilesType.GetFrameMasterFileRevisionsById>['args'];
  postGetActivePresignedFrameMasterFilesById: ApiType<MasterFilesType.PostGetActivePresignedFrameMasterFilesById>['args']; // (This is a query. use as a GET request)
  deleteFrameMasterfiles: ApiType<MasterFilesType.DeleteFrameMasterfiles>['args'];
  restoreFrameMasterfiles: ApiType<MasterFilesType.RestoreFrameMasterfiles>['args'];
  deleteFrameMasterfileRevision: ApiType<MasterFilesType.DeleteFrameMasterfileRevision>['args'];
  restoreFrameMasterfileRevision: ApiType<MasterFilesType.RestoreFrameMasterfileRevision>['args'];

  // Part Master Files
  postPartMasterFiles: ApiType<MasterFilesType.PostPartMasterFiles>['args'];
  getPartMasterFilesAll: ApiType<MasterFilesType.GetPartMasterFilesAll>['args'];
  getPartMasterFileRevisionsById: ApiType<MasterFilesType.GetPartMasterFileRevisionsById>['args'];
  postGetActivePresignedPartMasterFilesById: ApiType<MasterFilesType.PostGetActivePresignedPartMasterFilesById>['args']; // (This is a query. use as a GET request)
  deletePartMasterfiles: ApiType<MasterFilesType.DeletePartMasterfiles>['args'];
  restorePartMasterfiles: ApiType<MasterFilesType.RestorePartMasterfiles>['args'];
  deletePartMasterfileRevision: ApiType<MasterFilesType.DeletePartMasterfileRevision>['args'];
  restorePartMasterfileRevision: ApiType<MasterFilesType.RestorePartMasterfileRevision>['args'];
};
export type responses = {
  // Frame Master Files
  postFrameMasterFiles: ApiType<MasterFilesType.PostFrameMasterFiles>['response'];
  getFrameMasterFilesAll: ApiType<MasterFilesType.GetFrameMasterFilesAll>['response'];
  getFrameMasterFileRevisionsById: ApiType<MasterFilesType.GetFrameMasterFileRevisionsById>['response'];
  postGetActivePresignedFrameMasterFilesById: ApiType<MasterFilesType.PostGetActivePresignedFrameMasterFilesById>['response']; // (This is a query. use as a GET request)
  deleteFrameMasterfiles: ApiType<MasterFilesType.DeleteFrameMasterfiles>['response'];
  restoreFrameMasterfiles: ApiType<MasterFilesType.RestoreFrameMasterfiles>['response'];
  deleteFrameMasterfileRevision: ApiType<MasterFilesType.DeleteFrameMasterfileRevision>['response'];
  restoreFrameMasterfileRevision: ApiType<MasterFilesType.RestoreFrameMasterfileRevision>['response'];

  // Part Master Files
  postPartMasterFiles: ApiType<MasterFilesType.PostPartMasterFiles>['response'];
  getPartMasterFilesAll: ApiType<MasterFilesType.GetPartMasterFilesAll>['response'];
  getPartMasterFileRevisionsById: ApiType<MasterFilesType.GetPartMasterFileRevisionsById>['response'];
  postGetActivePresignedPartMasterFilesById: ApiType<MasterFilesType.PostGetActivePresignedPartMasterFilesById>['response']; // (This is a query. use as a GET request)
  deletePartMasterfiles: ApiType<MasterFilesType.DeletePartMasterfiles>['response'];
  restorePartMasterfiles: ApiType<MasterFilesType.RestorePartMasterfiles>['response'];
  deletePartMasterfileRevision: ApiType<MasterFilesType.DeletePartMasterfileRevision>['response'];
  restorePartMasterfileRevision: ApiType<MasterFilesType.RestorePartMasterfileRevision>['response'];
};

export const masterFilesApi = createApi({
  reducerPath: 'masterFilesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['MasterFiles'],
  endpoints: (builder) => ({
    // Frame Master Files
    postFrameMasterFiles: builder.mutation<
      responses['postFrameMasterFiles'],
      args['postFrameMasterFiles']
    >({
      query: ({ body, params }) => ({
        url: `/masterfiles/${params.project_id}/frame-masterfiles/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'MasterFiles',
          id: 'frame-master-files',
        },
      ],
      onQueryStarted: invalidateFrameDrawingConsumers,
    }),

    getFrameMasterFilesAll: builder.query<
      responses['getFrameMasterFilesAll'],
      args['getFrameMasterFilesAll']
    >({
      query: ({ params, query }) => ({
        url: `/masterfiles/${params.project_id}/frame-masterfiles/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'MasterFiles', id: 'frame-master-files' },
              {
                type: 'MasterFiles',
                id: `frame-master-files-${_arg.params.project_id}`,
              },
            ]
          : [],
    }),

    getFrameMasterFileRevisionsById: builder.query<
      responses['getFrameMasterFileRevisionsById'],
      args['getFrameMasterFileRevisionsById']
    >({
      query: ({ params, query }) => ({
        url: `/masterfiles/frame-masterfiles/${params.masterfile_id}/revisions/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'MasterFiles',
                id: 'frame-master-files',
              },
              {
                type: 'MasterFiles',
                id: `frame-master-file-revisions-${_arg.params.masterfile_id}`,
              },
            ]
          : [],
    }),

    postGetActivePresignedFrameMasterFilesById: builder.query<
      responses['postGetActivePresignedFrameMasterFilesById'], // (This is a query. use as a GET request)
      args['postGetActivePresignedFrameMasterFilesById']
    >({
      query: ({ body }) => ({
        url: `/masterfiles/frame-masterfiles/files/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'MasterFiles',
                id: 'frame-master-files',
              },
              {
                type: 'MasterFiles',
                id: `frame-master-file-revisions-${_arg.body.masterfile_ids}`,
              },
            ]
          : [],
    }),

    deleteFrameMasterfiles: builder.mutation<
      responses['deleteFrameMasterfiles'],
      args['deleteFrameMasterfiles']
    >({
      query: ({ body, params }) => ({
        url: `/masterfiles/${params.project_id}/frame-masterfiles/delete/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'frame-master-files' },
        {
          type: 'MasterFiles',
          id: `frame-master-files-${_arg.params.project_id}`,
        },
      ],
      onQueryStarted: invalidateFrameDrawingConsumers,
    }),

    restoreFrameMasterfiles: builder.mutation<
      responses['restoreFrameMasterfiles'],
      args['restoreFrameMasterfiles']
    >({
      query: ({ body, params }) => ({
        url: `/masterfiles/${params.project_id}/frame-masterfiles/restore/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'frame-master-files' },
        {
          type: 'MasterFiles',
          id: `frame-master-files-${_arg.params.project_id}`,
        },
      ],
      onQueryStarted: invalidateFrameDrawingConsumers,
    }),

    deleteFrameMasterfileRevision: builder.mutation<
      responses['deleteFrameMasterfileRevision'],
      args['deleteFrameMasterfileRevision']
    >({
      query: ({ params }) => ({
        url: `/masterfiles/${params.project_id}/frame-masterfiles/revisions/${params.revision_id}/`,
        method: 'DELETE',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'frame-master-files' },
        {
          type: 'MasterFiles',
          id: `frame-master-file-revisions-${_arg.params.masterfile_id}`,
        },
      ],
      onQueryStarted: invalidateFrameDrawingConsumers,
    }),

    restoreFrameMasterfileRevision: builder.mutation<
      responses['restoreFrameMasterfileRevision'],
      args['restoreFrameMasterfileRevision']
    >({
      query: ({ params }) => ({
        url: `/masterfiles/${params.project_id}/frame-masterfiles/revisions/${params.revision_id}/restore/`,
        method: 'POST',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'frame-master-files' },
        {
          type: 'MasterFiles',
          id: `frame-master-file-revisions-${_arg.params.masterfile_id}`,
        },
      ],
      onQueryStarted: invalidateFrameDrawingConsumers,
    }),

    // Part Master Files
    postPartMasterFiles: builder.mutation<
      responses['postPartMasterFiles'],
      args['postPartMasterFiles']
    >({
      query: ({ body, params }) => ({
        url: `/masterfiles/${params.project_id}/part-masterfiles/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'MasterFiles',
          id: 'part-master-files',
        },
      ],
    }),

    getPartMasterFilesAll: builder.query<
      responses['getPartMasterFilesAll'],
      args['getPartMasterFilesAll']
    >({
      query: ({ params, query }) => ({
        url: `/masterfiles/${params.project_id}/part-masterfiles/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'MasterFiles', id: 'part-master-files' },
              {
                type: 'MasterFiles',
                id: `part-master-files-${_arg.params.project_id}`,
              },
            ]
          : [],
    }),

    getPartMasterFileRevisionsById: builder.query<
      responses['getPartMasterFileRevisionsById'],
      args['getPartMasterFileRevisionsById']
    >({
      query: ({ params, query }) => ({
        url: `/masterfiles/part-masterfiles/${params.masterfile_id}/revisions/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'MasterFiles',
                id: 'part-master-files',
              },
              {
                type: 'MasterFiles',
                id: `part-master-file-revisions-${_arg.params.masterfile_id}`,
              },
            ]
          : [],
    }),

    postGetActivePresignedPartMasterFilesById: builder.query<
      responses['postGetActivePresignedPartMasterFilesById'], // (This is a query. use as a GET request)
      args['postGetActivePresignedPartMasterFilesById']
    >({
      query: ({ body }) => ({
        url: `/masterfiles/part-masterfiles/files/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'MasterFiles',
                id: 'part-master-files',
              },
              {
                type: 'MasterFiles',
                id: `part-master-file-revisions-${_arg.body.masterfile_ids}`,
              },
            ]
          : [],
    }),

    deletePartMasterfiles: builder.mutation<
      responses['deletePartMasterfiles'],
      args['deletePartMasterfiles']
    >({
      query: ({ body, params }) => ({
        url: `/masterfiles/${params.project_id}/part-masterfiles/delete/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'part-master-files' },
        {
          type: 'MasterFiles',
          id: `part-master-files-${_arg.params.project_id}`,
        },
      ],
    }),

    restorePartMasterfiles: builder.mutation<
      responses['restorePartMasterfiles'],
      args['restorePartMasterfiles']
    >({
      query: ({ body, params }) => ({
        url: `/masterfiles/${params.project_id}/part-masterfiles/restore/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'part-master-files' },
        {
          type: 'MasterFiles',
          id: `part-master-files-${_arg.params.project_id}`,
        },
      ],
    }),

    deletePartMasterfileRevision: builder.mutation<
      responses['deletePartMasterfileRevision'],
      args['deletePartMasterfileRevision']
    >({
      query: ({ params }) => ({
        url: `/masterfiles/${params.project_id}/part-masterfiles/revisions/${params.revision_id}/`,
        method: 'DELETE',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'part-master-files' },
        {
          type: 'MasterFiles',
          id: `part-master-file-revisions-${_arg.params.masterfile_id}`,
        },
      ],
    }),

    restorePartMasterfileRevision: builder.mutation<
      responses['restorePartMasterfileRevision'],
      args['restorePartMasterfileRevision']
    >({
      query: ({ params }) => ({
        url: `/masterfiles/${params.project_id}/part-masterfiles/revisions/${params.revision_id}/restore/`,
        method: 'POST',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'MasterFiles', id: 'part-master-files' },
        {
          type: 'MasterFiles',
          id: `part-master-file-revisions-${_arg.params.masterfile_id}`,
        },
      ],
    }),
  }),
});

export const {
  // Frame Master Files
  usePostFrameMasterFilesMutation,
  useGetFrameMasterFilesAllQuery,
  useGetFrameMasterFileRevisionsByIdQuery,
  usePostGetActivePresignedFrameMasterFilesByIdQuery,
  useDeleteFrameMasterfilesMutation,
  useRestoreFrameMasterfilesMutation,
  useDeleteFrameMasterfileRevisionMutation,
  useRestoreFrameMasterfileRevisionMutation,

  // Part Master Files
  usePostPartMasterFilesMutation,
  useGetPartMasterFilesAllQuery,
  useGetPartMasterFileRevisionsByIdQuery,
  usePostGetActivePresignedPartMasterFilesByIdQuery,
  useDeletePartMasterfilesMutation,
  useRestorePartMasterfilesMutation,
  useDeletePartMasterfileRevisionMutation,
  useRestorePartMasterfileRevisionMutation,
} = masterFilesApi;

export namespace MasterFilesApi {
  export const Api = masterFilesApi;
  export type Args = args;
  export type Responses = responses;
}
